/**
 * Individual payment request card component
 */
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { QrCode, Share2, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { getStatusBadge, formatExpiry } from './constants';

const RequestItem = ({ 
  request, 
  onShowQR, 
  onShare, 
  onCancel,
  isHistory = false 
}) => {
  if (isHistory) {
    return (
      <Card className="opacity-75" data-testid={`request-history-${request.id}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium">
                  {request.amount ? `$${request.amount.toFixed(2)}` : 'Any Amount'} {request.currency}
                </p>
                {getStatusBadge(request.status)}
              </div>
              {request.note && (
                <p className="text-sm text-muted-foreground">{request.note}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Created: {new Date(request.created_at).toLocaleDateString()}
                {request.total_received > 0 && ` • Received: $${request.total_received.toFixed(2)}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card data-testid={`request-${request.id}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
              onClick={() => onShowQR(request)}
              data-testid={`qr-trigger-${request.id}`}
            >
              <QrCode className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-heading font-semibold">
                  {request.amount ? `$${request.amount.toFixed(2)}` : 'Any Amount'} {request.currency}
                </p>
                {getStatusBadge(request.status)}
              </div>
              {request.note && (
                <p className="text-sm text-muted-foreground truncate">{request.note}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {formatExpiry(request.expires_at)} • Received: ${request.total_received?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onShowQR(request)} 
                className="rounded-lg"
                data-testid={`show-qr-${request.id}`}
              >
                <QrCode className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onShare(request)} 
                className="rounded-lg"
                data-testid={`share-${request.id}`}
              >
                <Share2 className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onCancel(request.id)} 
                className="rounded-lg text-destructive"
                data-testid={`cancel-${request.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RequestItem;
